import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger
} from './ui/dropdown-menu';
import { Bell, CheckCheck, Cake, Clock3, Megaphone, CalendarCheck2 } from 'lucide-react';
import { API_BASE as API_URL } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';

const typeIcon = (t) => {
  if (t === 'birthday') return <Cake className="w-4 h-4 text-pink-500 shrink-0" />;
  if (t === 'comp_off') return <Clock3 className="w-4 h-4 text-amber-500 shrink-0" />;
  if (t === 'leave') return <CalendarCheck2 className="w-4 h-4 text-emerald-500 shrink-0" />;
  return <Megaphone className="w-4 h-4 text-indigo-500 shrink-0" />;
};

const timeAgo = (iso) => {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ''; }
};

export default function NotificationBell() {
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const prevUnread = useRef(0);

  const headers = () => {
    const token = localStorage.getItem('hrms-token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const pollUnread = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/unread-count`, { headers: headers(), credentials: 'include' });
      const d = await safeJson(res);
      if (!res.ok) return;
      const n = d.unread || 0;
      // WhatsApp-style browser popup when new notifications arrive
      if (n > prevUnread.current && prevUnread.current >= 0 && 'Notification' in window && Notification.permission === 'granted') {
        try {
          const r2 = await fetch(`${API_URL}/api/notifications?limit=1`, { headers: headers(), credentials: 'include' });
          const latest = await safeJson(r2);
          if (r2.ok && latest[0]) {
            new Notification(latest[0].title, { body: latest[0].message, icon: '/logo192.png' });
          }
        } catch { /* ignore */ }
      }
      prevUnread.current = n;
      setUnread(n);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
    pollUnread();
    const t = setInterval(pollUnread, 45000);
    return () => clearInterval(t);
  }, [pollUnread]);

  const openList = async (o) => {
    setOpen(o);
    if (!o) return;
    try {
      const res = await fetch(`${API_URL}/api/notifications?limit=30`, { headers: headers(), credentials: 'include' });
      const d = await safeJson(res);
      if (res.ok) setItems(Array.isArray(d) ? d : []);
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/read-all`, { method: 'PUT', headers: headers(), credentials: 'include' });
      setUnread(0);
      prevUnread.current = 0;
      setItems(items.map(i => ({ ...i, read: true })));
    } catch { /* ignore */ }
  };

  return (
    <DropdownMenu open={open} onOpenChange={openList}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9 relative" data-testid="notifications-btn">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-unread-badge">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0" data-testid="notif-dropdown">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <p className="text-sm font-semibold">Notifications {unread > 0 && <Badge className="ml-1 bg-red-100 text-red-700">{unread} new</Badge>}</p>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-indigo-600 hover:underline flex items-center gap-1" data-testid="notif-mark-all-read">
              <CheckCheck className="w-3.5 h-3.5" />Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-10" data-testid="notif-empty">No notifications yet</p>
          ) : items.map(n => (
            <div key={n.notification_id}
              className={`flex gap-2.5 px-3 py-2.5 border-b last:border-0 ${n.read ? 'bg-white' : 'bg-indigo-50/60'}`}
              data-testid={`notif-item-${n.notification_id}`}>
              <div className="mt-0.5">{typeIcon(n.type)}</div>
              <div className="min-w-0">
                <p className="text-sm font-medium leading-tight">{n.title}</p>
                <p className="text-xs text-slate-600 mt-0.5 whitespace-pre-line break-words">{n.message}</p>
                <p className="text-[10px] text-slate-400 mt-1">{n.sender_name} · {timeAgo(n.created_at)}</p>
              </div>
              {!n.read && <span className="ml-auto mt-1 w-2 h-2 rounded-full bg-indigo-500 shrink-0" />}
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
